import type { Board } from 'jsxgraph';
import type { LabelPosition, LabelSpacing } from './label.model';

/**
 * Pixel offsets for each label position.
 * These are base offsets that place the label away from the element.
 *
 * For points: Applied directly from the point location.
 * For lines: Applied from the midpoint. Note that `left`/`right` are interpreted
 * as perpendicular to the line direction (relative to start → end points).
 */
const POSITION_OFFSETS: Record<LabelPosition, [number, number]> = {
  top: [0, 15],
  bottom: [0, -15],
  left: [-15, 0],
  right: [15, 0],
  topLeft: [-10, 10],
  topRight: [10, 10],
  bottomLeft: [-10, -10],
  bottomRight: [10, -10],
  center: [0, 0],
  canvasTopLeft: [12, -12],
  canvasTop: [0, -12],
  canvasTopRight: [-12, -12],
  canvasLeft: [12, 0],
  canvasCenter: [0, 0],
  canvasRight: [-12, 0],
  canvasBottomLeft: [12, 12],
  canvasBottom: [0, 12],
  canvasBottomRight: [-12, 12],
  alignedAbove: [0, 6],
  alignedBelow: [0, -6],
  alignedCenter: [0, 0],
};

/**
 * Compact offsets for labels that need to sit tight against elements (e.g. geometry edge labels).
 */
const COMPACT_OFFSETS: Record<LabelPosition, [number, number]> = {
  top: [0, 4],
  bottom: [0, -4],
  left: [-4, 0],
  right: [4, 0],
  topLeft: [-3, 3],
  topRight: [3, 3],
  bottomLeft: [-3, -3],
  bottomRight: [3, -3],
  center: [0, 0],
  canvasTopLeft: [12, -12],
  canvasTop: [0, -12],
  canvasTopRight: [-12, -12],
  canvasLeft: [12, 0],
  canvasCenter: [0, 0],
  canvasRight: [-12, 0],
  canvasBottomLeft: [12, 12],
  canvasBottom: [0, 12],
  canvasBottomRight: [-12, 12],
  alignedAbove: [0, 4],
  alignedBelow: [0, -4],
  alignedCenter: [0, 0],
};

/**
 * Anchor configuration for each label position.
 * Determines how the label text box is aligned relative to its position.
 */
const POSITION_ANCHORS: Record<LabelPosition, { anchorX: string; anchorY: string }> = {
  top: { anchorX: 'middle', anchorY: 'bottom' },
  bottom: { anchorX: 'middle', anchorY: 'top' },
  left: { anchorX: 'right', anchorY: 'middle' },
  right: { anchorX: 'left', anchorY: 'middle' },
  topLeft: { anchorX: 'right', anchorY: 'bottom' },
  topRight: { anchorX: 'left', anchorY: 'bottom' },
  bottomLeft: { anchorX: 'right', anchorY: 'top' },
  bottomRight: { anchorX: 'left', anchorY: 'top' },
  center: { anchorX: 'middle', anchorY: 'middle' },
  canvasTopLeft: { anchorX: 'left', anchorY: 'top' },
  canvasTop: { anchorX: 'middle', anchorY: 'top' },
  canvasTopRight: { anchorX: 'right', anchorY: 'top' },
  canvasLeft: { anchorX: 'left', anchorY: 'middle' },
  canvasCenter: { anchorX: 'middle', anchorY: 'middle' },
  canvasRight: { anchorX: 'right', anchorY: 'middle' },
  canvasBottomLeft: { anchorX: 'left', anchorY: 'bottom' },
  canvasBottom: { anchorX: 'middle', anchorY: 'bottom' },
  canvasBottomRight: { anchorX: 'right', anchorY: 'bottom' },
  alignedAbove: { anchorX: 'middle', anchorY: 'middle' },
  alignedBelow: { anchorX: 'middle', anchorY: 'middle' },
  alignedCenter: { anchorX: 'middle', anchorY: 'middle' },
};

/**
 * Get the pixel offset for a label position.
 *
 * @param position - The label position
 * @returns [dx, dy] pixel offsets
 */
export function getPositionOffset(position: LabelPosition): [number, number] {
  return POSITION_OFFSETS[position];
}

/**
 * Get the anchor configuration for a label position.
 * The anchor determines how the label is aligned relative to its position.
 *
 * @param position - The label position
 * @returns Anchor configuration for JSXGraph text element
 */
export function getAnchor(position: LabelPosition): { anchorX: string; anchorY: string } {
  return POSITION_ANCHORS[position];
}

/**
 * Extra pixel boost applied on top of base offsets for slope-driven labels
 * (lines, curves). These labels anchor directly on the element, so they need
 * more clearance than point labels to avoid overlapping the stroke.
 */
const SLOPE_DRIVEN_BOOST: Record<LabelPosition, [number, number]> = {
  top: [0, 8],
  bottom: [0, -8],
  left: [-8, 0],
  right: [8, 0],
  topLeft: [-6, 6],
  topRight: [6, 6],
  bottomLeft: [-6, -6],
  bottomRight: [6, -6],
  center: [0, 0],
  canvasTopLeft: [0, 0],
  canvasTop: [0, 0],
  canvasTopRight: [0, 0],
  canvasLeft: [0, 0],
  canvasCenter: [0, 0],
  canvasRight: [0, 0],
  canvasBottomLeft: [0, 0],
  canvasBottom: [0, 0],
  canvasBottomRight: [0, 0],
  alignedAbove: [0, 0],
  alignedBelow: [0, 0],
  alignedCenter: [0, 0],
};

/**
 * Combine position offset with custom offset.
 * The custom offset allows fine-tuning of label placement beyond the base position.
 *
 * @param position - The label position (determines base offset)
 * @param customOffset - Additional [dx, dy] offset in pixels for fine-tuning
 * @param slopeDriven - When true, applies extra clearance for labels on lines/curves
 * @param spacing - Offset distance mode: 'compact' for tight labels, 'normal' for standard
 * @returns Combined [dx, dy] pixel offsets
 *
 * @example
 * // Position 'right' with additional 5px down adjustment
 * getCombinedOffset('right', [0, -5]); // Returns [15, -5]
 */
export function getCombinedOffset(
  position: LabelPosition,
  customOffset: [number, number],
  slopeDriven = false,
  spacing: LabelSpacing = 'normal',
): [number, number] {
  const offsets = spacing === 'compact' ? COMPACT_OFFSETS : POSITION_OFFSETS;
  const positionOffset = offsets[position];
  // Compact spacing skips the slope-driven boost — the tight offsets already clear the stroke.
  const boost = slopeDriven && spacing !== 'compact' ? SLOPE_DRIVEN_BOOST[position] : [0, 0];
  return [positionOffset[0] + boost[0] + customOffset[0], positionOffset[1] + boost[1] + customOffset[1]];
}

/**
 * Check if a label position is canvas-relative (anchored to the viewport, not an element).
 */
export function isCanvasPosition(position: LabelPosition): boolean {
  return position.startsWith('canvas');
}

/**
 * Get base math coords for a canvas position from the board bbox.
 */
export function getCanvasBaseCoords(position: LabelPosition, board: Board): [number, number] {
  const [xMin, yMax, xMax, yMin] = board.getBoundingBox();
  const xMid = (xMin + xMax) / 2;
  const yMid = (yMin + yMax) / 2;
  const hasLeft = position.includes('Left');
  const hasRight = position.includes('Right');
  const hasTop = position.includes('Top');
  const hasBottom = position.includes('Bottom');
  const x = hasLeft ? xMin : hasRight ? xMax : xMid;
  const y = hasTop ? yMax : hasBottom ? yMin : yMid;
  return [x, y];
}

/**
 * Check if a label position is an aligned position (text rotates along the element direction).
 */
export function isAlignedPosition(position: LabelPosition): boolean {
  return position === 'alignedAbove' || position === 'alignedBelow' || position === 'alignedCenter';
}

/**
 * Compute the perpendicular pixel offset for aligned labels.
 * The offset is perpendicular to the element's slope direction.
 *
 * @param position - One of the aligned positions
 * @param slopeRadians - The element's slope in radians
 * @param spacing - Offset distance mode
 * @returns [dx, dy] pixel offsets perpendicular to the slope
 */
export function getAlignedOffset(
  position: LabelPosition,
  slopeRadians: number,
  spacing: LabelSpacing = 'normal',
): [number, number] {
  if (position === 'alignedCenter') {
    return [0, 0];
  }

  // Distance perpendicular to the line
  const dist = spacing === 'compact' ? 4 : 6;
  // "above" means perpendicular to the left of the direction vector (rotate +90°)
  const sign = position === 'alignedAbove' ? 1 : -1;
  const perpAngle = slopeRadians + (sign * Math.PI) / 2;
  return [Math.cos(perpAngle) * dist, Math.sin(perpAngle) * dist];
}

/**
 * Normalize slope to [-π/2, π/2] so rotated text always reads left-to-right.
 */
export function normalizeTextAngle(slopeRadians: number): number {
  let a = slopeRadians % (2 * Math.PI);
  if (a > Math.PI) {
    a -= 2 * Math.PI;
  }
  if (a < -Math.PI) {
    a += 2 * Math.PI;
  }
  // Flip if pointing leftward so text reads L→R
  if (a > Math.PI / 2) {
    a -= Math.PI;
  }
  if (a < -Math.PI / 2) {
    a += Math.PI;
  }
  return a;
}

/**
 * Resolve an `anchorAt` value to a numeric fraction in [0, 1].
 *
 * @param value - `'start'` → 0, `'middle'`/undefined → 0.5, `'end'` → 1, number → clamped [0, 1]
 */
export function resolveAnchorAt(value: number | 'start' | 'middle' | 'end' | undefined): number {
  if (value === undefined || value === 'middle') {
    return 0.5;
  }
  if (value === 'start') {
    return 0;
  }
  if (value === 'end') {
    return 1;
  }
  return Math.max(0, Math.min(1, value));
}
